import Title from '@cohort/components-xps/components/Title';
import CenteredContent from '@cohort/wallet/components/layout/CenteredContent';
import {CreditCardForm} from '@cohort/wallet/components/store/checkout/CreditCardForm';
import {FreeClaimForm} from '@cohort/wallet/components/store/checkout/FreeClaimForm';
import {Subtitle} from '@cohort/wallet/components/Subtitle';
import {useUserStore} from '@cohort/wallet/hooks/stores/user';
import useStoreContext from '@cohort/wallet/hooks/useStoreContext';
import StoreLeftPanel from '@cohort/wallet/layouts/store/StoreLeftPanel';
import StorePageContainer from '@cohort/wallet/layouts/store/StorePage';
import {getStoreOrderRoute} from '@cohort/wallet/lib/Pages';
import sendMessageToParentWindow from '@cohort/wallet/lib/WindowMessaging';
import type {OrderWDto} from '@cohort/wallet-schemas/order';
import {useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';

const CheckoutPage = (): JSX.Element => {
  const {setBuyingStep, buyingStep, setTransitioning, campaign, price} = useStoreContext();

  const {t} = useTranslation('pages', {keyPrefix: 'store.checkoutPage'});
  const {search} = useLocation();
  const redeemCode = useMemo(() => {
    const query = new URLSearchParams(search);
    return query.get('redeemCode') ?? undefined;
  }, [search]);
  const navigate = useNavigate();
  const user = useUserStore(store => store.user);

  const updateBuyingStep = useCallback(() => {
    if (user === null) {
      setBuyingStep({
        total: 3,
        current: 1,
      });
    } else if (buyingStep.total === 0) {
      setBuyingStep({
        total: 2,
        current: 1,
      });
    } else if (buyingStep.total === 3) {
      setBuyingStep({
        total: buyingStep.total,
        current: 2,
      });
    }
  }, [buyingStep.total, setBuyingStep, user]);

  useEffect(() => {
    updateBuyingStep();
  }, [campaign, search, updateBuyingStep]);

  const onHandleSuccess = useCallback(
    (order: OrderWDto) => {
      setBuyingStep({
        ...buyingStep,
        current: buyingStep.current + 1,
      });
      setTransitioning(true);
      sendMessageToParentWindow({
        event: 'order.created',
        payload: {
          orderId: order.id,
        },
      });
      navigate(`${getStoreOrderRoute(campaign.store.slug, order.id)}${search}`, {
        state: {order},
        replace: true,
      });
    },
    [buyingStep, campaign.store.slug, navigate, search, setBuyingStep, setTransitioning]
  );

  const isPaid = campaign.store.pricing === 'paid';

  return (
    <StorePageContainer
      title={isPaid ? t('checkout') : t('claim')}
      tracking={{
        namespace: 'store.checkout',
      }}
      leftPanel={<StoreLeftPanel />}
    >
      <CenteredContent className="max-w-xl space-y-5 px-4 md:p-0">
        <div className="text-center md:text-left">
          <Title content={isPaid ? t('checkout') : t('claim')} />
          <Subtitle
            content={isPaid ? t('checkoutSubtitle') : t('claimSubtitle')}
            classes="text-center md:text-left"
          />
        </div>
        {isPaid && price !== null ? (
          <CreditCardForm
            campaign={campaign}
            redeemCode={redeemCode}
            successCallback={(order: OrderWDto) => onHandleSuccess(order)}
            price={price}
          />
        ) : (
          <FreeClaimForm
            campaign={campaign}
            successCallback={(order: OrderWDto) => onHandleSuccess(order)}
            redeemCode={redeemCode}
          />
        )}
      </CenteredContent>
    </StorePageContainer>
  );
};

export default CheckoutPage;
